import { useContext } from 'react';

import { ServiceIdentifier } from 'inversify';
import {
  TypedUseSelectorHook,
  useSelector as useReduxSelector,
  NoInfer,
} from 'react-redux';

import { Inject, IocContext } from '@vk-hr-tek/core/ioc';

import type { RootState } from '../store';

export const useSelector = <
  TSelected,
  TParam,
  TState extends RootState = RootState,
>(
  selector:
    | ((state: TState, inject: Inject, parameter: TParam) => TSelected)
    | ((state: TState, inject: Inject, parameter?: TParam) => TSelected),
  parameter?: TParam,
  equalityFn?: (left: NoInfer<TSelected>, right: NoInfer<TSelected>) => boolean,
) => {
  const container = useContext(IocContext);

  if (!container) {
    throw new Error('Ioc container is empty');
  }

  const inject = <T>(identifier: ServiceIdentifier<T>): T => {
    return container.get<T>(identifier);
  };

  const useAppSelector: TypedUseSelectorHook<TState> = useReduxSelector;

  return useAppSelector(
    (state) => selector(state, inject, parameter as TParam),
    equalityFn,
  );
};
